import {isEnumValue} from '@Utils/enum.util';
import {isOptionalString, isShownMoment} from '@Components/ai-chat-window/lib/stream-component-prop-guards';
import {isPlainObject} from '@Utils/object.util';
import {formatFriendlyMoment} from '@Utils/friendly-moment.util';
import type {EntityCardMetaEntry, EntityCardProps, EntityCardStatus} from './entity-card.types';
import {ENTITY_CARD_META_SEPARATOR, EntityCardEntityType, EntityCardStatusTone} from './entity-card.types';

export const parseEntityCardProps = (props: Record<string, unknown>): EntityCardProps | null => {
  if (!isMetaList(props.meta) || !isOptionalString(props.alert) || !isOptionalString(props.title) || !isOptionalString(props.mediaUrl) || !isOptionalString(props.icon)) {
    return null;
  }

  const status = parseOptionalEntityCardStatus(props.status);
  if (status === null || !isOptionalMediaCount(props.mediaCount) || !isOptionalMoment(props.refreshAt)) {
    return null;
  }

  return {
    title: props.title,
    meta: props.meta,
    alert: props.alert,
    mediaUrl: props.mediaUrl,
    icon: props.icon,
    mediaCount: props.mediaCount,
    status,
    entityType: isEnumValue(EntityCardEntityType, props.entityType) ? props.entityType : undefined,
    entity: isPlainObject(props.entity) ? props.entity : undefined,
    refreshable: props.refreshable === true,
    refreshAt: props.refreshAt,
  };
};

const isMetaList = (value: unknown): value is EntityCardMetaEntry[] => {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string' || isShownMoment(entry));
};

/** The card's meta line as it shows it now: its facts, a moment worded on today's date, then its alert. */
export const shownMetaLineOf = (meta: EntityCardMetaEntry[], alert: string | undefined): string => {
  const facts = meta.map((entry) => (typeof entry === 'string' ? entry : formatFriendlyMoment(entry)));

  return (alert === undefined || alert === '' ? facts : [...facts, alert]).join(ENTITY_CARD_META_SEPARATOR);
};

export const parseOptionalEntityCardStatus = (value: unknown): EntityCardStatus | undefined | null => {
  if (value === undefined) {
    return undefined;
  }

  if (!isPlainObject(value) || typeof value.label !== 'string' || !isEnumValue(EntityCardStatusTone, value.tone)) {
    return null;
  }

  return {label: value.label, tone: value.tone};
};

export const isOptionalMediaCount = (value: unknown): value is number | undefined => {
  return value === undefined || (Number.isInteger(value) && typeof value === 'number' && value >= 0);
};

const isOptionalMoment = (value: unknown): value is number | undefined => {
  return value === undefined || (typeof value === 'number' && Number.isInteger(value) && value > 0);
};

/** The thumbnail shows the first design; the badge counts the rest. */
export const extraMediaCount = (mediaCount: number | undefined): number => {
  return mediaCount === undefined ? 0 : Math.max(mediaCount - 1, 0);
};
